import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';

import i18n from '../../i18n';
import { IRootState } from '../../redux/core-store';
import { closeDirectAnswerEditModal } from '../../redux/slices/modals/direct-answer-edit-modal-slice';
import { abortAskQuestion } from '../../redux/thunks/ask-thunk';
import { abortProactiveQuestion } from '../../redux/thunks/proactive-answer-thunk';
import { LAST_ANSWERS_STORAGE_KEY } from '../../services/storage/storage-keys-list';
import { storageService } from '../../services/storage/storage-service';
import { getToastService } from '../../services/toast/toast-service';
import { getLogger } from '../../utils/logger';
import { baseApi } from '../base-api';
import {
    ActionTypeV2,
    ApiTagTypes,
    AppsV2InputType,
    IAiHelperUserAction,
    IAppV2Event,
    IAppV2Result,
    IFailureEvent,
    IGetAppInvocationV2Params,
    IGetAppInvocationV2Response,
    IGetUserAppsV2Params,
    IGetUserAppsV2Response,
    IInvokeAppResponse,
    IInvokeAppV2Body,
    IInvokeAppV2Params,
    ILastAnswersData,
    ResponseStatus,
    StreamFailureReason,
    StreamStatus,
    TriggerType,
} from '../types';

import { stream } from './utils/stream';

const logger = getLogger('AppsV2Endpoint');
export const appsUrl = `${process.env.REACT_APP_APPS_SERVICE_URL}`;

const baseApiWithTags = baseApi.enhanceEndpoints({
    addTagTypes: [ApiTagTypes.APPS, ApiTagTypes.USER],
});

const getTriggerTypeFromAppsV2InputType = (inputType: AppsV2InputType): TriggerType => {
    switch (inputType) {
        case AppsV2InputType.API_CALL:
            return TriggerType.SCRAPPING;
        case AppsV2InputType.FULL_PAGE:
            return TriggerType.FULL_WEBPAGE;
        case AppsV2InputType.SELECTED_TEXT:
            return TriggerType.TEXT_MARK;
    }
};

export const appsEndpoints = baseApiWithTags.injectEndpoints({
    endpoints: (builder) => ({
        getUserAppsV2: builder.query<IAiHelperUserAction[], IGetUserAppsV2Params>({
            queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
                const { customer_project_id } = args;
                const response = await baseQuery({
                    url: appsUrl,
                    params: { customer_project_id },
                    method: 'GET',
                });

                const { error, data } = response as QueryReturnValue<
                    IGetUserAppsV2Response,
                    FetchBaseQueryError,
                    FetchBaseQueryMeta
                >;

                if (error) {
                    if (error.status !== ResponseStatus.NOT_FOUND) {
                        const message = i18n.t('error.api.get-apps', { ns: 'errors' });
                        getToastService().error(message);
                    }

                    return { error };
                }

                // Format apps into Apps V1 interface
                const apps: IAiHelperUserAction[] = data.apps.map((app) => {
                    const { input, name: title, uid: action_id, category, ...rest } = app;
                    return {
                        title,
                        action_id,
                        action_type: ActionTypeV2.CUSTOM,
                        group: category ? { title: category.name, ...category } : null,
                        trigger_type: getTriggerTypeFromAppsV2InputType(input.type),
                        ...input.config,
                        ...rest,
                    };
                });
                return { data: apps };
            },
            providesTags: [ApiTagTypes.APPS],
        }),
        getLastAppInvocation: builder.query<IAppV2Result | null, IGetAppInvocationV2Params>({
            queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
                const { customer_project_id } = args;
                const response = await baseQuery({
                    url: `${appsUrl}/latest`,
                    params: { customer_project_id },
                    method: 'GET',
                });

                const { error, data } = response as QueryReturnValue<
                    IGetAppInvocationV2Response,
                    FetchBaseQueryError,
                    FetchBaseQueryMeta
                >;

                if (error) {
                    if (error.status !== ResponseStatus.NOT_FOUND) {
                        const message = i18n.t('error.api.get-apps', { ns: 'errors' });
                        getToastService().error(message);
                    }

                    return { data: null };
                }
                return { data: { ...data.app_invocation, status: StreamStatus.DONE } };
            },
            providesTags: [ApiTagTypes.APPS],
        }),
        invokeApp: builder.mutation<IInvokeAppResponse, IInvokeAppV2Params>({
            queryFn: async (args, { dispatch }) => {
                const { invocation_id, app } = args;

                // We want to abort running proactive process when a user is pressing an ai helper app
                dispatch(abortProactiveQuestion());
                dispatch(abortAskQuestion());
                dispatch(closeDirectAnswerEditModal());

                const lastAnswersData: ILastAnswersData = {
                    question: '',
                    answers: [],
                    question_id: invocation_id,
                    expect_direct_answer: true,
                    custom_action: app,
                    action_type: ActionTypeV2.CUSTOM,
                    proactiveMeta: null,
                };

                await storageService.setStorageValue(LAST_ANSWERS_STORAGE_KEY, lastAnswersData);

                return {
                    data: {
                        answers: [],
                        expect_direct_answer: true,
                        question_id: invocation_id,
                    },
                };
            },
            async onCacheEntryAdded(
                args: IInvokeAppV2Params,
                { cacheDataLoaded, cacheEntryRemoved, getState, dispatch }
            ) {
                await cacheDataLoaded;
                const { customer_project_id, app, controller, query, url, invocation_id } = args;
                const { action_id: app_id } = app;

                const state = getState() as IRootState;
                const { token } = state.auth;

                if (!token) {
                    logger.warn('No token, will NOT create a Stream');
                    return;
                }

                if (!app_id) {
                    logger.warn('No app_id');
                    return;
                }

                if (!customer_project_id) {
                    logger.warn('No customer_project_id');
                    return;
                }

                dispatch(
                    appsEndpoints.util.updateQueryData('getLastAppInvocation', { customer_project_id }, () => {
                        return { app_id, content: '', invocation_id, status: StreamStatus.PENDING, meta: app };
                    })
                );

                const onEvent = (event: IAppV2Event | IFailureEvent | null) => {
                    if (!event) {
                        return;
                    }
                    if ('error' in event) {
                        return onError(event);
                    }
                    // Update the cached data for invoke app
                    dispatch(
                        appsEndpoints.util.updateQueryData('getLastAppInvocation', { customer_project_id }, (draft) => {
                            if (!draft) {
                                return;
                            }

                            if (draft.invocation_id !== invocation_id) {
                                return;
                            }

                            // First Chunk
                            if (!draft?.content) {
                                draft.content = event.content;
                                draft.app_id = app_id;
                                draft.invocation_id = event.invocation_id;
                                draft.status = StreamStatus.RUNNING;
                            } else {
                                draft.content += event.content;
                                if (event.status) {
                                    draft.status = event.status;
                                }
                            }
                        })
                    );
                };

                const onError = (event: IFailureEvent) => {
                    dispatch(
                        appsEndpoints.util.updateQueryData('getLastAppInvocation', { customer_project_id }, (draft) => {
                            if (!draft) {
                                return;
                            }
                            const content = i18n.t(`error.chat.${event.reason.toLowerCase().replace('_', '-')}`, {
                                ns: 'errors',
                            });
                            draft.invocation_id = invocation_id;
                            draft.content = content;
                            draft.status = StreamStatus.FAILED;
                        })
                    );
                };

                const onAbort = () => {
                    dispatch(
                        appsEndpoints.util.updateQueryData('getLastAppInvocation', { customer_project_id }, (draft) => {
                            if (!draft) {
                                return;
                            }
                            draft.status = StreamStatus.ABORTED;
                        })
                    );
                };

                const invokeAppUrl = new URL(`${appsUrl}/${app_id}/invoke`);
                invokeAppUrl.searchParams.append('customer_project_id', customer_project_id);

                const body: IInvokeAppV2Body = { query, url, invocation_id };

                stream<IInvokeAppV2Body, IAppV2Event>(token, invokeAppUrl, body, controller, onEvent).catch((error) =>
                    onError({
                        error: JSON.stringify(error),
                        status: StreamStatus.FAILED,
                        reason: StreamFailureReason.UNKNOWN,
                    })
                );

                const signal = controller.signal;
                signal.addEventListener('abort', onAbort);

                await cacheEntryRemoved;
                signal.removeEventListener('abort', onAbort);
            },
        }),
    }),
});

export const { useGetUserAppsV2Query, useInvokeAppMutation, useGetLastAppInvocationQuery } = appsEndpoints;
